import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Container,
  keyframes,
  Button,
  Grid,
  Paper,
  Divider,
  Link,
  useTheme,
  useMediaQuery,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  Backdrop,
  Radio,
  RadioGroup,
  FormControl,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DownloadIcon from '@mui/icons-material/Download';
import HomeIcon from '@mui/icons-material/Home';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { CaseInputType, useCaseIntake } from '../context/CaseIntakeContext';
import { useAuthContext } from '../context/AuthContext';
import { processinput } from '../queries/processinput';
import { submitcase } from '../queries/submitcase';
import { CancerType, UserType } from '../schema';
import { getCaseUntilGenerated } from '../queries/getCaseUntilGenerated';
import { retryAsync } from '../utils/retry';
import { confirmTermsOfService } from '../queries/confirmtermsofservice';
import { submitsurvey } from '../queries/submitsurvey';
import { usePersistentNavigate } from '../utils/navigation';
import { FileUpload } from '../submitcase';
import ChatBot from "react-chatbotify";
import { sendmessage } from '../queries/sendmessage';

// Define keyframes for animations
const pulse = keyframes`
  0% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const CaseReport: React.FC = () => {
  const navigate = usePersistentNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('case_id');
  const isAdminView = searchParams.get('from') === 'admin';
  const reportContentRef = useRef<HTMLDivElement>(null);
  const submitted = useRef(false);
  const [displayText, setDisplayText] = useState("Thinking");
  const [loadingStage, setLoadingStage] = useState(0); // 0: parsing, 1: thinking, 2: display
  const [reportContent, setReportContent] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { caseContextText, caseContextJson, caseInputType, contextCancerType, caseContextUploadedFiles } = useCaseIntake();

  const { accessToken, setTermsOfServiceAccepted, termsOfServiceAccepted, completedRegistration, setAccessToken, logout, userType } = useAuthContext();

  // States for the disclosure popup
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [disclosureChecked, setDisclosureChecked] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [isReturningUser, setIsReturningUser] = useState(false);

  // Add this state with your other state declarations 
  const [logMessages, setLogMessages] = useState<string[]>([]);

  // Add these states with other states
  const [impactRating, setImpactRating] = useState<number | null>(null);
  const [surveySubmitted, setSurveySubmitted] = useState(false);

  // Add this state with your other states (near existing impactRating state)
  const [feedbackText, setFeedbackText] = useState<string>("");

  // Add this state
  const [submittingSurvey, setSubmittingSurvey] = useState(false);
  const [submitCaseResponse, setSubmitCaseResponse] = useState<any>(null);

  // Handle disclosure visibility
  const showDisclosureIfApplicable = () => {
    if (termsOfServiceAccepted) {
      setIsReturningUser(true);
    } else {
      setIsReturningUser(false);
    }
    setShowDisclosure(true);
  };

  // Replace the runProcessingLog function with this version

  const runProcessingLog = () => {
    const messages = [
      "Parsing input data...",
      "> Input parsed.",
      "Conducting research...",
      "> Searching PubMed...",
      "> Searching NCCN...",
      "> Searching ASCO...",
      "> Searching other journals...",
      "> Searching clinical trials...",
      "> Relevant data indexed.",
      "Developing opinion...",
      "> Initial treatment constructed.",
      "Considering alternatives...",
      "> Evaluating trade-offs...",
      "> Revising recommendation...",
      "> Recommendation finalized.",
      "Generating report..."
    ];

    const timings = [
      0,         // immediately
      10000,      // 10 seconds
      10100,      // 10.1 seconds
      11000,     // 11 seconds
      19000,     // 19 seconds
      27000,     // 27 seconds
      32000,     // 32 seconds
      44000,     // 44 seconds
      58000,     // 58 seconds
      58100,     // 58.1 seconds develop
      75000,     // 75 seconds, initial
      75100,     // 75.1 seconds evaluating
      94000,     // 94 seconds calcuating
      101000,    // 101 seconds revising
      106000,    // 106 seconds primary
      106100,    // 106.1 seconds generating
    ];

    // Clear any existing messages
    setLogMessages([]);

    // Add the first message immediately
    setLogMessages([messages[0]]);

    // Schedule the rest of the messages at specified times
    messages.forEach((message, index) => {
      // Skip the first message as we've already added it
      if (index === 0) return;

      setTimeout(() => {
        setLogMessages(prev => [...prev, message]);
      }, timings[index]);
    });
  };

  // Submit case and process the response
  const submitCaseAsync = async () => {
    setDisplayText("Thinking");
    setLoadingStage(1);
    runProcessingLog(); // Start the processing log

    if (!accessToken) {
      navigate('/login');
      return;
    }

    const isAdmin = userType === UserType.ADMIN;

    if (!completedRegistration && !isAdmin) {
      navigate('/complete-registration');
      return;
    }

    try {
      if (caseInputType === CaseInputType.TEXTAREA) {
        setLoadingStage(0);

        const processInputResponse = await retryAsync(() => processinput({
          textArea: caseContextText,
          fileBytes: [],
          fileNames: []
        }, accessToken || '', setAccessToken, logout));

        if (processInputResponse.containsPii) {
          navigate('/', {
            state: {
              rejectedText: caseContextText,
              toastMessage: "PrecisCa AI detected PII in your input, which is prohibited in our Terms of Service. Please try again with anonymized input."
            }
          });
          return;
        }

        if (processInputResponse.invalidInput) {
          navigate('/', {
            state: {
              rejectedText: caseContextText,
              toastMessage: "PrecisCa AI is only available for licensed medical professionals seeking second opinions on cancer cases. Please re-submit a request with a valid cancer case."
            }
          });
          return;
        }

        setLoadingStage(1);

        const submitCaseResponse = await retryAsync(() => submitcase({
          caseJson: processInputResponse.caseJson,
          cancerType: CancerType.UNSPECIFIED_CANCER_TYPE,
          files: []
        }, accessToken || '', setAccessToken, logout));

        // Store response for later use
        setSubmitCaseResponse(submitCaseResponse);

        console.log('Submit case response:', submitCaseResponse);
        const c = await getCaseUntilGenerated(submitCaseResponse.caseId, accessToken || '', setAccessToken, logout);

        setReportContent(c.markdown);
        setLoadingStage(2);
        showDisclosureIfApplicable();

        sessionStorage.setItem('lastSubmittedCaseResponse', JSON.stringify(submitCaseResponse));
      }
      else if (caseInputType === CaseInputType.FORM) {
        const submitCaseResponse = await retryAsync(() => submitcase({
          caseJson: caseContextJson,
          cancerType: contextCancerType,
          files: []
        }, accessToken || '', setAccessToken, logout));

        // Store response for later use
        setSubmitCaseResponse(submitCaseResponse);

        console.log('Submit case response:', submitCaseResponse);
        const c = await getCaseUntilGenerated(submitCaseResponse.caseId, accessToken || '', setAccessToken, logout);

        setReportContent(c.markdown);
        setLoadingStage(2);
        showDisclosureIfApplicable();

        sessionStorage.setItem('lastSubmittedCaseResponse', JSON.stringify(submitCaseResponse));
      }
      else if (caseInputType === CaseInputType.FILE) {
        setLoadingStage(0); // Parsing input

        const fileBytes: Uint8Array[] = [];
        const fileNames: string[] = [];
        const filesToSubmit: FileUpload[] = [];
        for (const uploadedFile of (caseContextUploadedFiles || [])) {
          const file = uploadedFile.file;
          const buffer = await file.arrayBuffer();

          fileBytes.push(new Uint8Array(buffer));
          fileNames.push(file.name);

          const bytes = new Uint8Array(buffer);

          filesToSubmit.push({
            filename: file.name,
            content: bytes
          });
        }

        const processInputResponse = await retryAsync(() => processinput({
          textArea: "",
          fileBytes: fileBytes,
          fileNames: fileNames,
        }, accessToken || '', setAccessToken, logout));

        setLoadingStage(1); // Thinking

        const submitCaseResponse = await retryAsync(() => submitcase({
          caseJson: processInputResponse.caseJson,
          cancerType: CancerType.UNSPECIFIED_CANCER_TYPE,
          files: filesToSubmit,
        }, accessToken || '', setAccessToken, logout));

        // Store response for later use
        setSubmitCaseResponse(submitCaseResponse);

        console.log('Submit case response:', submitCaseResponse);
        const c = await getCaseUntilGenerated(submitCaseResponse.caseId, accessToken || '', setAccessToken, logout);

        setReportContent(c.markdown);
        setLoadingStage(2);
        showDisclosureIfApplicable();

        sessionStorage.setItem('lastSubmittedCaseResponse', JSON.stringify(submitCaseResponse));
      }
    } catch (error) {
      console.error("Failed to process case after multiple retries:", error);
      setReportContent('# Error\nFailed to process your case. Please log out, log back in, and try again.');
      setLoadingStage(2);
      showDisclosureIfApplicable();
    }
  };

  // Handle accepting the disclosure
  const handleAcceptDisclosure = async () => {
    if (!isReturningUser) {
      await confirmTermsOfService(accessToken || '', setAccessToken, logout);
      setTermsOfServiceAccepted(true);
    }
    setShowDisclosure(false);
  };

  // Check if all checkboxes are checked
  const allChecked = privacyChecked && disclosureChecked && agreementChecked;

  // Submit the case when the component loads
  useEffect(() => {
    window.scrollTo(0, 0);

    if (caseId) {
      setLoadingStage(1);
      runProcessingLog();
      getCaseUntilGenerated(caseId, accessToken || '', setAccessToken, logout).then((c) => {
        setReportContent(c.markdown);
        setLoadingStage(2);
        if (userType !== UserType.ADMIN) {
          showDisclosureIfApplicable();
        }
      }).catch(error => {
        console.error("Failed to get case after multiple retries:", error);
        setReportContent('# Error\nFailed to retrieve case report. Please try again later.');
        setLoadingStage(2);
      });
      return;
    }

    if (sessionStorage.getItem('lastSubmittedCaseResponse')) {
      const savedResponse = JSON.parse(sessionStorage.getItem('lastSubmittedCaseResponse') || '{}');
      submitted.current = true;
      setSubmitCaseResponse(savedResponse);
      setLoadingStage(1);

      getCaseUntilGenerated(savedResponse.caseId, accessToken || '', setAccessToken, logout).then((c) => {
        setReportContent(c.markdown);
        setLoadingStage(2);
      });

      return;
    }

    if (!submitted.current) {
      submitted.current = true;
      submitCaseAsync();
    }
  }, [caseId, accessToken, setAccessToken, logout, userType]);

  // Handle PDF download
  const handleDownloadPDF = async () => {
    if (!reportContentRef.current) return;

    try {
      const canvas = await html2canvas(reportContentRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('case-report.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  // Update the handleSurveySubmit function
  const handleSurveySubmit = async () => {
    if (impactRating !== null && submitCaseResponse?.caseId) {
      try {
        setSubmittingSurvey(true);

        await submitsurvey({
          caseId: submitCaseResponse.caseId,
          rating: impactRating,
          comment: feedbackText || undefined // Only include if non-empty
        }, accessToken || '', setAccessToken, logout);

        console.log('Survey submitted with rating:', impactRating, 'and comment:', feedbackText);
        setSurveySubmitted(true);
      } catch (error) {
        console.error('Error submitting survey:', error);
      } finally {
        setSubmittingSurvey(false);
      }
    }
  };

  const settings = {
    general: { embedded: false },
    chatHistory: { storageKey: "caseConversation" },
    header: {
      title: "PrecisCa AI",
    },
    notification: {
      disabled: true,
    },
    tooltip: {
      mode: "CLOSE",
      text: "Click here to ask a follow-up question related to this case.",
    }
  }

  // ... (inside component)

  const handleSendMessage = async (params: any) => {
    const userInput = params.userInput;
    let currentCaseId = caseId;

    if (!currentCaseId && submitCaseResponse?.caseId) {
      currentCaseId = submitCaseResponse.caseId;
    }

    if (!currentCaseId) {
      return "Error: Case ID not found. Please try reloading the page.";
    }

    try {
      const response = await sendmessage({
        caseId: currentCaseId,
        chatHistory: "", // We might need to manage history if the API expects it, or if ChatBot manages it we might need to format it. 
        // For now, passing empty as per request "Pass in the context of the conversation (i.e. chat_history in SendMessageRequest)..."
        // Wait, the user said "Pass in the context of the conversation... along with case data... and the most recent message".
        // If the API is stateless regarding history, we need to send it. 
        // react-chatbotify might provide history. 
        // Let's check params.
        incomingMessage: userInput
      }, accessToken || '', setAccessToken, logout);

      return response.response;
    } catch (error) {
      console.error("Error sending message:", error);
      return "Sorry, I encountered an error processing your message.";
    }
  }

  const flow = {
    start: {
      message: "Welcome to your PreciscaAI case conversation! Feel free to ask may any follow up questions about your case.",
      path: "end_loop"
    },
    end_loop: {
      message: handleSendMessage,
      path: "end_loop"
    }
  }

  return (
    <>
      {/* Top Navigation Bar */}
      <AppBar position="static" color="default" elevation={0} sx={{ backgroundColor: 'white', borderBottom: '1px solid #e6e6e6' }}>
        <Toolbar sx={{
          minHeight: { xs: '56px', sm: '64px' },
          justifyContent: 'space-between',
          padding: { xs: '0 16px', sm: '0 24px' }
        }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
          >
            <Typography variant="h5" component="div" sx={{
              color: '#2c5282',
              fontWeight: 'bold',
              fontSize: { xs: '1.2rem', sm: '1.5rem' }
            }}>
              PRECI<span style={{ color: '#4299e1' }}>SCA</span>
            </Typography>
          </Box>

          <IconButton
            color="inherit"
            sx={{ color: '#2c5282' }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Disclosure Dialog */}
      <Dialog
        open={showDisclosure}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        disablePortal
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            p: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid #e2e8f0',
            pb: 1.5,
            pt: 2,
            color: '#2c5282',
            fontWeight: 600,
            fontSize: { xs: '1.2rem', sm: '1.5rem' }
          }}
        >
          Important Disclosure
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Typography variant="body1" sx={{ mb: 3, fontWeight: 500, color: '#2d3748' }}>
            <br />This proprietary material does not constitute medical or health advice to a patient but rather is intended to provide an educational perspective. PrecisCa does not engage in the practice of medicine. Please review our{' '}
            <Link
              href="/legal/PrecisCa.AI Privacy Policy.pdf"
              underline="hover"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#4299e1', fontWeight: 500 }}
            >
              Privacy Policy
            </Link>,{' '}
            <Link
              href="/legal/PrecisCa.AI Disclaimer.pdf"
              underline="hover"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#4299e1', fontWeight: 500 }}
            >
              Disclaimer
            </Link>, and{' '}
            <Link
              href="/legal/PrecisCa.AI Terms and Conditions.pdf"
              underline="hover"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#4299e1', fontWeight: 500 }}
            >
              Terms of Service
            </Link>{' '}
            to learn more.
          </Typography>

          {/* Only show checkboxes for first-time users */}
          {!isReturningUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={privacyChecked}
                    onChange={(e) => setPrivacyChecked(e.target.checked)}
                    sx={{
                      color: '#a0aec0',
                      '&.Mui-checked': { color: '#4299e1' }
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ color: '#2d3748' }}>
                    I have read and agree to the Privacy Policy.
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={disclosureChecked}
                    onChange={(e) => setDisclosureChecked(e.target.checked)}
                    sx={{
                      color: '#a0aec0',
                      '&.Mui-checked': { color: '#4299e1' }
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ color: '#2d3748' }}>
                    I have read and agree to the Disclosure Statement.
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreementChecked}
                    onChange={(e) => setAgreementChecked(e.target.checked)}
                    sx={{
                      color: '#a0aec0',
                      '&.Mui-checked': { color: '#4299e1' }
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ color: '#2d3748' }}>
                    I have read and agree to the User Agreement.
                  </Typography>
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            variant="contained"
            onClick={handleAcceptDisclosure}
            disabled={!isReturningUser && !allChecked}
            fullWidth
            sx={{
              bgcolor: (isReturningUser || allChecked) ? '#4299e1' : '#cbd5e0',
              color: 'white',
              py: 1.2,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 500,
              borderRadius: '8px',
              boxShadow: (isReturningUser || allChecked) ? '0 2px 5px rgba(66, 153, 225, 0.3)' : 'none',
              opacity: (isReturningUser || allChecked) ? 1 : 0.7,
              '&:hover': (isReturningUser || allChecked) ? {
                bgcolor: '#2c5282',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(44, 82, 130, 0.4)'
              } : {}
            }}
          >
            {isReturningUser ? "Continue to Case Report" : "Accept to See Case Report"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backdrop for blurring when disclosure is shown */}
      <Backdrop
        sx={{
          zIndex: theme.zIndex.drawer - 1,
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(255, 255, 255, 0.6)'
        }}
        open={showDisclosure}
      />

      {/* Main Content */}
      <Container
        maxWidth="md"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: loadingStage < 2 ? 'center' : 'flex-start',
          minHeight: 'calc(100vh - 64px)',
          padding: { xs: 2, sm: 3 },
          mb: 8,
          overflowY: 'auto',
          filter: showDisclosure ? 'blur(2px)' : 'none',
          transition: 'filter 0.3s ease'
        }}
      >
        {loadingStage < 2 ? (
          <>
            {/* Processing Log */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 60, sm: 70 }, // Increased to move below gray bar
                left: { xs: 2, sm: 10 }, // Match container padding
                padding: 2,
                maxWidth: { xs: '85%', sm: '50%' },
                textAlign: 'left',
                zIndex: 10,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '8px',
                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.00)'
              }}
            >
              {logMessages.map((message, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    color: message.startsWith('>') ? '#4299e1' : '#2d3748',
                    mb: 0.5,
                    opacity: 0.9,
                    fontWeight: message.startsWith('>') ? 400 : 500,
                    whiteSpace: 'nowrap' // Prevent text wrapping
                  }}
                >
                  {message}
                </Typography>
              ))}
            </Box>

            {/* Loading animation */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 200,
                height: 200
              }}
            >
              {/* SVG Circle Animation */}
              <Box sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                animation: `${rotate} 2s linear infinite`
              }}>
                <svg width="100%" height="100%" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="transparent"
                    stroke="#4299e1"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeDasharray="283"
                    strokeDashoffset="283"
                  >
                    <animate
                      attributeName="stroke-dashoffset"
                      from="283"
                      to="0"
                      dur="2s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </svg>
              </Box>

              {/* Pulsing Text */}
              <Typography
                variant="h4"
                sx={{
                  color: '#2c5282',
                  fontWeight: '500',
                  animation: `${pulse} 2s ease-in-out infinite`,
                  fontSize: { xs: '1.1rem', sm: '1.5rem' },
                  transition: 'opacity 0.5s ease',
                  minWidth: '140px',
                  textAlign: 'center',
                  whiteSpace: 'pre-line'
                }}
              >
                {displayText}
              </Typography>
            </Box>

            {/* Processing time message */}
            <Typography
              variant="body2"
              sx={{
                color: '#718096',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                mt: 5,
                textAlign: 'center'
              }}
            >
              Case processing may take up to 2 minutes. <br />Thank you for your patience.
            </Typography>
          </>
        ) : (
          <Box sx={{ width: '100%' }}>
            {/* Mobile download button */}
            <Box sx={{
              display: { xs: 'flex', sm: 'none' },
              justifyContent: 'center',
              mb: 3
            }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
                sx={{
                  color: '#2c5282',
                  borderColor: '#2c5282',
                  py: 1,
                  px: 2,
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.2s ease',
                  width: '100%',
                  '&:hover': {
                    borderColor: '#4299e1',
                    color: '#4299e1',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  }
                }}
              >
                Download PDF
              </Button>
            </Box>

            {/* Markdown content with styling */}
            <Paper
              elevation={0}
              ref={reportContentRef}
              sx={{
                p: { xs: 2, sm: 4 },
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                mb: 3
              }}
            >
              <Box sx={{
                color: '#4a5568',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '1rem',
                lineHeight: 1.7,
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  color: '#2c5282',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  mb: 2,
                  mt: 3
                },
                '& h1': {
                  fontSize: { xs: '1.7rem', sm: '2.1rem' },
                  borderBottom: '1px solid #e2e8f0',
                  pb: 1
                },
                '& h2': {
                  fontSize: { xs: '1.4rem', sm: '1.8rem' }
                },
                '& h3': {
                  fontSize: { xs: '1.2rem', sm: '1.5rem' }
                },
                '& p': {
                  mb: 2
                },
                '& a': {
                  color: '#4299e1',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                },
                '& ul, & ol': {
                  paddingLeft: 3,
                  mb: 2
                },
                '& li': {
                  mb: 1
                },
                '& blockquote': {
                  borderLeft: '4px solid #e2e8f0',
                  pl: 2,
                  fontStyle: 'italic',
                  my: 2
                },
                '& code': {
                  fontFamily: 'monospace',
                  backgroundColor: '#f7fafc',
                  padding: '2px 4px',
                  borderRadius: 1
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto'
                },
                '& hr': {
                  border: 0,
                  height: '1px',
                  backgroundColor: '#e2e8f0',
                  my: 3
                }
              }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    table: ({ node, ...props }) => (
                      <Box sx={{ overflowX: 'auto', my: 3 }}>
                        <table
                          style={{
                            borderCollapse: 'collapse',
                            width: '100%',
                            border: '1px solid #e2e8f0'
                          }}
                          {...props}
                        />
                      </Box>
                    ),
                    thead: ({ node, ...props }) => (
                      <thead style={{ backgroundColor: '#f7fafc' }} {...props} />
                    ),
                    tbody: ({ node, ...props }) => (
                      <tbody {...props} />
                    ),
                    tr: ({ node, ...props }) => (
                      <tr
                        style={{
                          borderBottom: '1px solid #e2e8f0',
                        }}
                        {...props}
                      />
                    ),
                    th: ({ node, ...props }) => (
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontWeight: 600,
                          borderRight: '1px solid #e2e8f0',
                          color: '#2c5282'
                        }}
                        {...props}
                      />
                    ),
                    td: ({ node, ...props }) => (
                      <td
                        style={{
                          padding: '12px 16px',
                          borderRight: '1px solid #e2e8f0'
                        }}
                        {...props}
                      />
                    )
                  }}
                >
                  {reportContent}
                </ReactMarkdown>
              </Box>
            </Paper>

            {/* Survey Question */}
            {loadingStage === 2 && !surveySubmitted && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  mb: 3,
                  backgroundColor: '#f8fafc'
                }}
              >
                <Box sx={{ width: '100%', px: { xs: 1, sm: 3 } }}>
                  {/* Rating buttons with numbers */}
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: '#2c5282',
                      fontWeight: 500,
                      fontSize: { xs: '1rem', sm: '1.1rem' }
                    }}
                  >
                    How much will this opinion impact your ultimate treatment plan?
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%'
                    }}
                  >
                    {[1, 2, 3, 4, 5].map((value) => (
                      <Box
                        key={value}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: '20%'
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            mb: 0.5,
                            color: impactRating === value ? '#4299e1' : '#718096'
                          }}
                        >
                          {value}
                        </Typography>
                        <Radio
                          checked={impactRating === value}
                          onChange={() => setImpactRating(value)}
                          value={value}
                          name="rating-radio-buttons"
                          sx={{
                            color: '#a0aec0',
                            '&.Mui-checked': { color: '#4299e1' }
                          }}
                        />
                      </Box>
                    ))}
                  </Box>

                  {/* Desktop labels */}
                  <Box
                    sx={{
                      display: { xs: 'none', sm: 'flex' },
                      justifyContent: 'space-between',
                      width: '100%',
                      mt: 1
                    }}
                  >
                    {[
                      "No impact",
                      "Minimal impact",
                      "Moderate impact",
                      "Significant impact",
                      "Major impact"
                    ].map((label, index) => (
                      <Typography
                        key={index}
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: impactRating === index + 1 ? 500 : 400,
                          color: impactRating === index + 1 ? '#4299e1' : '#718096',
                          width: '20%',
                          textAlign: 'center'
                        }}
                      >
                        {label}
                      </Typography>
                    ))}
                  </Box>

                  {/* Mobile labels (only 1, 3, 5) */}
                  <Box
                    sx={{
                      display: { xs: 'flex', sm: 'none' },
                      width: '100%',
                      mt: 1
                    }}
                  >
                    {/* Container that holds all 5 positions but only shows labels for 1, 3, 5 */}
                    <Box sx={{ width: '20%', textAlign: 'center' }}>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: impactRating === 1 ? 500 : 400,
                          color: impactRating === 1 ? '#4299e1' : '#718096',
                          whiteSpace: 'pre-line',
                          lineHeight: 1.2
                        }}
                      >
                        No{'\n'}impact
                      </Typography>
                    </Box>

                    {/* Empty space for position 2 */}
                    <Box sx={{ width: '20%' }}></Box>

                    <Box sx={{ width: '20%', textAlign: 'center' }}>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: impactRating === 3 ? 500 : 400,
                          color: impactRating === 3 ? '#4299e1' : '#718096',
                          whiteSpace: 'pre-line',
                          lineHeight: 1.2
                        }}
                      >
                        Moderate{'\n'}impact
                      </Typography>
                    </Box>

                    {/* Empty space for position 4 */}
                    <Box sx={{ width: '20%' }}></Box>

                    <Box sx={{ width: '20%', textAlign: 'center' }}>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: impactRating === 5 ? 500 : 400,
                          color: impactRating === 5 ? '#4299e1' : '#718096',
                          whiteSpace: 'pre-line',
                          lineHeight: 1.2
                        }}
                      >
                        Major{'\n'}impact
                      </Typography>
                    </Box>
                  </Box>

                  {/* Optional Feedback Section */}
                  <Box sx={{ mt: 4, width: '100%', px: { xs: 0, sm: 0 } }}> {/* Removed any potential extra padding */}
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        color: '#2c5282',
                        fontWeight: 500,
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      }}
                    >
                      Would you like to leave optional feedback on the quality of the recommendation?
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Optional feedback"
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '& fieldset': {
                            borderColor: '#e2e8f0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#cbd5e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4299e1',
                          },
                        },
                        '& .MuiInputBase-input::placeholder': {
                          fontSize: '0.85rem',
                          opacity: 0.7
                        }
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    disabled={impactRating === null || submittingSurvey}
                    onClick={handleSurveySubmit}
                    sx={{
                      bgcolor: impactRating !== null ? '#4299e1' : '#cbd5e0',
                      color: 'white',
                      py: 1,
                      px: 4,
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                      fontWeight: 500,
                      borderRadius: '8px',
                      boxShadow: impactRating !== null ? '0 2px 5px rgba(66, 153, 225, 0.3)' : 'none',
                      opacity: impactRating !== null ? 1 : 0.7,
                      '&:hover': impactRating !== null ? {
                        bgcolor: '#2c5282',
                      } : {}
                    }}
                  >
                    {submittingSurvey ? 'Submitting...' : 'Submit Survey'}
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Thank you message after survey submission */}
            {loadingStage === 2 && surveySubmitted && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  mb: 3,
                  backgroundColor: '#f0f9ff'
                }}
              >
                <Typography
                  variant="body1"
                  align="center"
                  sx={{
                    color: '#2c5282',
                    fontWeight: 500
                  }}
                >
                  Thank you for your feedback!
                </Typography>
              </Paper>
            )}

            {/* Bottom buttons */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: { xs: 0, sm: 0 },
              gap: { xs: 3, sm: 4 },
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              {/* Desktop: Download PDF button */}
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
                sx={{
                  color: '#2c5282',
                  borderColor: '#2c5282',
                  py: { xs: 1.2, sm: 1.5 },
                  px: { xs: 3, sm: 4 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 500,
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  width: { xs: '100%', sm: 'auto' },
                  transition: 'all 0.2s ease',
                  display: { xs: 'none', sm: 'flex' },
                  '&:hover': {
                    borderColor: '#4299e1',
                    color: '#4299e1',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  }
                }}
              >
                Download PDF
              </Button>

              {isAdminView ? (
                <Button
                  variant="contained"
                  onClick={() => navigate('/admin')}
                  sx={{
                    bgcolor: '#4299e1',
                    color: 'white',
                    py: { xs: 1.2, sm: 1.5 },
                    px: { xs: 3, sm: 4 },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    fontWeight: 500,
                    borderRadius: '8px',
                    boxShadow: '0 2px 5px rgba(66, 153, 225, 0.3)',
                    width: { xs: '100%', sm: 'auto' },
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#2c5282',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(44, 82, 130, 0.4)',
                    },
                  }}
                >
                  Return to Admin Portal
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<HomeIcon />}
                  onClick={() => navigate('/')}
                  sx={{
                    bgcolor: '#4299e1',
                    color: 'white',
                    py: { xs: 1.2, sm: 1.5 },
                    px: { xs: 3, sm: 4 },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    fontWeight: 500,
                    borderRadius: '8px',
                    boxShadow: '0 2px 5px rgba(66, 153, 225, 0.3)',
                    width: { xs: '100%', sm: 'auto' },
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#2c5282',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(44, 82, 130, 0.4)',
                    },
                  }}
                >
                  Submit Another Case
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#f1f5f9', py: { xs: 4, sm: 6 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 3, sm: 5 }}>
            {/* Logo Column */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h5" sx={{
                color: '#2c5282',
                fontWeight: 'bold',
                mb: 2,
                fontSize: { xs: '1.2rem', sm: '1.5rem' }
              }}>
                PRECI<span style={{ color: '#4299e1' }}>SCA</span>
              </Typography>
              <Typography variant="body2" sx={{
                color: '#4a5568',
                mb: 2,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Elevating cancer care through <br></br> virtual second opinions.
              </Typography>
            </Grid>

            {/* Products Column */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{
                color: '#2c5282',
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                Products
              </Typography>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                AI Reviews
              </Link>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Doctor Opinions
              </Link>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Video Library
              </Link>
              <Link href="/legal/PrecisCa.AI Terms and Conditions.pdf" underline="hover" sx={{
                display: 'block',
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Terms of Service
              </Link>
            </Grid>

            {/* Company Column */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{
                color: '#2c5282',
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                Company
              </Typography>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                About
              </Link>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Our Faculty
              </Link>
              <Link href="/legal/PrecisCa.AI Privacy Policy.pdf" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Privacy Policy
              </Link>
              <Link href="/legal/PrecisCa.AI Disclaimer.pdf" underline="hover" sx={{
                display: 'block',
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Disclaimer
              </Link>
            </Grid>

            {/* Newsletter Column */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{
                color: '#2c5282',
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                Stay Updated
              </Typography>
              <Typography variant="body2" sx={{
                color: '#4a5568',
                mb: 2,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Stay abreast of the latest PRECISCA developments and new products.
              </Typography>
              <Paper
                component="form"
                sx={{
                  display: 'flex',
                  p: '2px 4px',
                  alignItems: 'center',
                  width: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 1
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Email"
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      px: 1,
                      py: { xs: 0.3, sm: 0.5 },
                      fontSize: { xs: '0.8rem', sm: '1rem' }
                    }
                  }}
                />
                <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                <Button
                  type="submit"
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: { xs: 0.3, sm: 0.5 },
                    bgcolor: '#4299e1',
                    color: 'white',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&:hover': { bgcolor: '#2c5282' }
                  }}
                >
                  Subscribe
                </Button>
              </Paper>
            </Grid>
          </Grid>

          {/* Copyright Section */}
          <Box sx={{ mt: { xs: 4, sm: 6 }, pt: { xs: 2, sm: 3 }, borderTop: '1px solid #e2e8f0' }}>
            <Grid
              container
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'center', sm: 'center' }}
              spacing={{ xs: 2, sm: 0 }}
            >
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Typography variant="body2" sx={{
                  color: '#4a5568',
                  textAlign: { xs: 'center', sm: 'left' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}>
                  © PRECISCA 2025. All rights reserved.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Box sx={{
                  display: 'flex',
                  gap: { xs: 2, sm: 3 },
                  justifyContent: { xs: 'center', sm: 'flex-end' }
                }}>
                  <Link href="/legal/PrecisCa.AI Disclaimer.pdf" underline="hover" sx={{
                    color: '#4a5568',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    Disclaimer
                  </Link>
                  <Link href="/legal/PrecisCa.AI Terms and Conditions.pdf" underline="hover" sx={{
                    color: '#4a5568',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    Terms of Use
                  </Link>
                  <Link href="/legal/PrecisCa.AI Privacy Policy.pdf" underline="hover" sx={{
                    color: '#4a5568',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    Privacy Policy
                  </Link>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
      {loadingStage === 2 && <ChatBot settings={settings} flow={flow} />}

    </>
  );
};

export default CaseReport;
